import { api_configuration } from "../../common";
import api_cs = api_configuration.types.client_state.alan_api;
import DestructorTree from "../DestructorTree";
import { IdPathBuilder, Raw_Id_Path_Head } from "../navigation/IdPathBuilder";
import * as Text from "../state/Text";
import * as Collection from "../state/Collection";
import { registerRelativeIdPath } from "../state/registerClientState";
import * as referenceResolver from "../state 2 interface/referenceResolver";
import supportsSelfReference from "../state/supportsSelfReference";
import { entry_reference_definition, descendant_object_path } from "../state/expressions";
import * as step from "../state/step";
import {
	Scalar,
	ScalarProvider,
	WeakCache,
	createVariable,
	MutableVariable,
	ReadonlyVariable,
	createExpressionBuilder,
} from "../../../../../stream";
import { Pending, Unknown, Result, Failed, isResolved } from "../Status";
import { entryReferenceSynchronized } from "../state/expressionSynchronized";
import { getGuiText } from "../guiModel";
import * as microtaskQueue from "../microtaskQueue";
import { setModified } from "./SaveModifiedStatus";
import { iText } from "../state/Text";
import ClientStateObjectProxy from "./ClientStateObjectProxy";

function updateLastKeyInRawIdPath(path: Raw_Id_Path_Head, new_key: string): string | undefined {
	switch (path["has steps"][0]) {
		case "no":
			return new_key;
		case "yes": {
			const step = path["has steps"][1];
			const tail = updateLastKeyInRawIdPath(step.tail, new_key);
			switch (step.type[0]) {
				case "group":
					return tail;
				case "state":
					return tail;
				case "collection entry":
					if (tail !== undefined) {
						step.type[1].id = tail;
					}
					return undefined;
			}
		}
	}
}

function createIdPathBuilderFromRawIdPath(
	raw_path: Raw_Id_Path_Head,
	path_builder = new IdPathBuilder()
): IdPathBuilder {
	switch (raw_path["has steps"][0]) {
		case "no":
			return path_builder;
		case "yes": {
			const step = raw_path["has steps"][1];
			switch (step.type[0]) {
				case "group":
					path_builder = path_builder.group(step.type[1].group);
					break;
				case "state":
					path_builder = path_builder.state(step.type[1]["state group"], step.type[1].state);
					break;
				case "collection entry":
					path_builder = path_builder.collectionEntry(step.type[1].collection, step.type[1].id);
					break;
			}
			return createIdPathBuilderFromRawIdPath(step.tail, path_builder);
		}
	}
}

function synchronized(reference: Reference): Scalar<Result<"yes">> {
	switch (
		reference.parent.parent.inferences.property_type().properties.type.state.node.output.value_source().variant.name
	) {
		case "derived":
			return "yes";
		case "base": {
			const stream = createVariable(
				entryReferenceSynchronized(reference.parent, reference.inferences.is_navigable().properties.expression)(),
				false
			);
			stream.subscribe({
				update: (synchronized) => {
					if (reference.destroyed) return;
					switch (synchronized) {
						case Pending:
							break;
						case Failed:
							reference.parent.properties.backend_known.switch({
								no: () => undefined,
								yes: (backend_known) =>
									reference.parent.properties.is_editable.switch({
										no: () => undefined,
										yes: ($) => {
											const is_modified = $.properties.modified.ensure("yes", {
												value: backend_known.properties.value,
												saving: ["no", {}],
											});
											setModified(is_modified);
											return is_modified;
										},
									}),
							});
							break;
						case Unknown:
						case "yes":
							reference.parent.properties.is_editable.switch({
								no: () => undefined,
								yes: ($) => $.properties.modified.ensure("no", {}),
							});
							break;
					}
				},
			});
			reference.onDestroy(() => stream.destroy());
			return stream;
		}
	}
}

type Reference = api_cs.Cyes__navigable;
type ResolveStatus = ["failed to resolve"] | ["is busy resolving"] | ["resolved", api_cs.Cnode] | ["unresolved"];

export class ReferenceInterface extends ClientStateObjectProxy {
	readonly isMandatory: boolean;
	readonly propagateKeyUpdates: boolean;
	readonly selfReferenceAllowed: boolean;
	readonly backendValue: MutableVariable<Result<IdPathBuilder>>;
	readonly synchronized: Scalar<Result<"yes">>;
	readonly collectionContext: ReadonlyVariable<Result<api_cs.Ccollection>>;
	readonly referencedEntry: MutableVariable<Result<api_cs.Centries__collection>>;
	readonly expression: ReadonlyVariable<Result<api_cs.Cnode>>;
	readonly referencedNode: ReadonlyVariable<Result<api_cs.Cnode>>;
	readonly status: ReadonlyVariable<ResolveStatus>;

	constructor(readonly reference: Reference) {
		super();
		const definition = reference.inferences.is_navigable();
		const expression = definition.properties.expression;
		const resolve_status = reference.properties.resolve_status;
		this.isMandatory = definition.properties.behaviour.properties.type.switch({
			mandatory: () => true,
			optional: () =>
				getGuiText(definition.parent)?.properties.navigable.switch({
					no: () => false,
					yes: ($) =>
						$.properties.validate.switch({
							no: false,
							yes: true,
						}),
				}) ?? false,
		});
		this.propagateKeyUpdates = definition.properties.behaviour.properties.type.switch({
			mandatory: () => true,
			optional: () =>
				definition.properties.behaviour_specialization.switch({
					none: () => false,
					"propagate key to value": () => true,
				}),
		});
		this.selfReferenceAllowed = supportsSelfReference(expression);
		this.backendValue = createVariable(Unknown, true);
		this.synchronized = synchronized(reference);
		this.collectionContext = createVariable(entry_reference_definition(expression)(reference.component_root), false);
		this.referencedEntry = createVariable(
			createExpressionBuilder(this.collectionContext).pipe(
				step.sync((collection) =>
					createExpressionBuilder(Text.value(this.reference.parent))
						.pipe(step.sync((key, destructor) => Collection.openEntry(collection, key, destructor)()))
						.pipe(
							step.sync((entry, destructor) => {
								if (!this.entryIsAllowed(entry)) return Failed;
								iText(entry.properties.key.ref).setR2M(this, destructor);
								return entry;
							})
						)()
				)
			)(),
			true
		);
		this.expression = createVariable(
			createExpressionBuilder(this.referencedEntry)
				.pipe(step.sync((entry) => entry.properties.node))
				.pipe(descendant_object_path(expression.properties.tail))() as ScalarProvider<Result<api_cs.Cnode>>,
			false
		);
		this.referencedNode = createVariable(
			createExpressionBuilder(this.synchronized).pipe((synchronized) => {
				switch (synchronized) {
					case Pending:
						return Pending;
					case Unknown:
					case "yes":
						return createExpressionBuilder(this.backendValue).pipe(
							step.sync((id_path, destructor) => registerRelativeIdPath(id_path, reference.component_root, destructor))
						)();
					case Failed:
						return this.expression;
				}
			})(),
			false
		);
		this.status = createVariable(
			createExpressionBuilder(this.referencedNode)
				.pipe((node): ResolveStatus => {
					if (resolve_status.destroyed) return ["unresolved"];
					switch (node) {
						case Pending:
							resolve_status.properties.status.ensure("is busy resolving", {});
							return ["is busy resolving"];
						case Unknown:
							resolve_status.properties.status.ensure("unresolved", {});
							return ["unresolved"];
						case Failed:
							resolve_status.properties.status.ensure("failed to resolve", {});
							return ["failed to resolve"];
						default:
							resolve_status.properties.status.ensure("resolved", {});
							return ["resolved", node];
					}
				})
				.fallback(["unresolved"])(),
			false,
			() => {
				if (resolve_status.destroyed) return;
				resolve_status.properties.status.ensure("unresolved", {});
			}
		);
		referenceResolver.subscribeToReference(resolve_status);
		reference.onDestroy(() => {
			this.expression.destroy();
			this.collectionContext.destroy();
			this.referencedEntry.destroy();
			this.referencedNode.destroy();
			this.destructor?.destroy();
		});
	}

	entryIsAllowed(entry: api_cs.Centries__collection) {
		return this.selfReferenceAllowed || entry !== this.reference.component_root.input.entity().cast("entry");
	}

	setServerValue(backend_value: Result<IdPathBuilder>): void {
		this.backendValue.set(Unknown);
		microtaskQueue.add(() => {
			if (this.reference.destroyed) return;
			this.backendValue.set(backend_value);
		});
	}

	updateKeyInServerValue(value: string): void {
		const id_path = this.backendValue.value;
		if (!isResolved(id_path)) return;
		const raw_path = id_path.build();
		updateLastKeyInRawIdPath(raw_path, value);
		this.setServerValue(createIdPathBuilderFromRawIdPath(raw_path));
	}

	destructor: DestructorTree | undefined;
	setReferencedEntry(entry: api_cs.Centries__collection) {
		this.referencedEntry.set(entry);
		this.destructor?.destroy();
		this.destructor = new DestructorTree();
		iText(entry.properties.key.ref).setR2M(this, this.destructor);
	}

	discard() {
		this.destructor?.destroy();
		this.referencedEntry.discard();
	}
}

export const iReference = WeakCache<Reference, ReferenceInterface>((reference) => new ReferenceInterface(reference));
